import { useLiveQuery } from "dexie-react-hooks";
import { CircleCheck, CornerDownLeft, FolderPlus, Hash, Inbox, Keyboard, Plus, Search, SquareCheck, Star, Tag, type LucideIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { colorHex } from "@todoer/shared";
import { db } from "../lib/db.js";
import { NAV_FOOTER, NAV_PRIMARY, NAV_TOOLS, NavIcon } from "./nav.js";
import { Dialog } from "./overlay.js";
import { openTaskParams } from "./TaskList.js";
import { cx, Kbd } from "./ui.js";

interface Item { id: string; group: string; label: string; hint?: string; icon: ReactNode; run: () => void; recentKey?: string }

const RECENT_KEY = "todoer.paletteRecent";
const loadRecent = (): string[] => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]"); } catch { return []; } };
const pushRecent = (id: string) => { try { localStorage.setItem(RECENT_KEY, JSON.stringify([id, ...loadRecent().filter((x) => x !== id)].slice(0, 5))); } catch { /* ignore */ } };
const ic = (I: LucideIcon, color?: string) => <I size={16} aria-hidden style={color ? { color } : undefined} />;

/** Ctrl/Cmd+K: jump anywhere, run a command, or find a task. Recent choices come first. */
export function CommandPalette({ onClose, onQuickAdd, onNewProject, onHelp }: { onClose: () => void; onQuickAdd: () => void; onNewProject: () => void; onHelp?: () => void }) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const [, setParams] = useSearchParams();
  const listRef = useRef<HTMLUListElement>(null);

  const data = useLiveQuery(async () => {
    const [projects, labels, filters, tasks] = await Promise.all([db.projects.toArray(), db.labels.toArray(), db.filters.toArray(), db.tasks.toArray()]);
    return { projects, labels, filters, tasks };
  }, []);

  const items = useMemo<Item[]>(() => {
    const go = (path: string) => () => { navigate(path); onClose(); };
    const needle = q.trim().toLowerCase();
    const match = (s: string) => !needle || s.toLowerCase().includes(needle);
    const navItems: Item[] = [...NAV_PRIMARY, ...NAV_TOOLS, ...NAV_FOOTER].map((d) => ({
      id: `nav${d.to}`, group: "Go to", label: d.label, hint: d.shortcut, icon: <NavIcon icon={d.icon} size={16} />, run: go(d.to),
    }));
    const actions: Item[] = [
      { id: "add", group: "Actions", label: "Add task", hint: "Q", icon: ic(Plus, "var(--color-brand)"), run: () => { onClose(); onQuickAdd(); } },
      { id: "newp", group: "Actions", label: "Add project", icon: ic(FolderPlus), run: () => { onClose(); onNewProject(); } },
      ...(onHelp ? [{ id: "help", group: "Actions", label: "Keyboard shortcuts", hint: "?", icon: ic(Keyboard), run: () => { onClose(); onHelp(); } }] : []),
    ];
    const all: Item[] = [...actions, ...navItems];
    if (data) {
      all.push(...data.projects.filter((p) => !p.isInbox).map((p) => ({
        id: `p-${p.id}`, group: "Projects", label: p.name, hint: p.isArchived ? "archived" : undefined, icon: ic(Hash, colorHex(p.color)), run: go(`/project/${p.id}`),
      })));
      all.push(...data.labels.map((l) => ({ id: `l-${l.id}`, group: "Labels", label: `@${l.name}`, icon: ic(Tag, colorHex(l.color)), run: go(`/label/${l.id}`) })));
      all.push(...data.filters.map((f) => ({ id: `f-${f.id}`, group: "Filters", label: f.name, icon: ic(Star, colorHex(f.color)), run: go(`/filter/${f.id}`) })));
    }
    const recentIds = loadRecent();
    if (!needle) {
      const recent = recentIds.map((id) => all.find((i) => i.id === id)).filter((i): i is Item => !!i).map((i) => ({ ...i, id: `r-${i.id}`, group: "Recent", recentKey: i.id }));
      return [...recent, ...actions, ...navItems, ...all.filter((i) => i.group === "Projects").slice(0, 6)];
    }
    const tasks = data
      ? data.tasks.filter((t) => t.content.toLowerCase().includes(needle) || t.description.toLowerCase().includes(needle))
        .sort((a, b) => Number(!!a.completedAt) - Number(!!b.completedAt)).slice(0, 8)
        .map((t) => ({ id: `t-${t.id}`, group: "Tasks", label: t.content, hint: t.completedAt ? "completed" : undefined, icon: ic(t.completedAt ? CircleCheck : SquareCheck), run: () => { onClose(); setParams(openTaskParams(t.id)); } }))
      : [];
    const searchAll: Item = { id: "search", group: "Tasks", label: `Search all tasks for “${q.trim()}”`, icon: ic(Search), run: go(`/search?q=${encodeURIComponent(q.trim())}`) };
    const matched = all.filter((i) => match(i.label)).slice(0, 30);
    return [...tasks, searchAll, ...matched];
  }, [q, data, navigate, onClose, onQuickAdd, onNewProject, onHelp, setParams]);

  useEffect(() => setActive(0), [q]);
  useEffect(() => { listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: "nearest" }); }, [active]);

  const run = (it: Item | undefined) => { if (!it) return; pushRecent(it.recentKey ?? it.id); it.run(); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    if (e.key === "Enter") { e.preventDefault(); run(items[active]); }
  };

  let lastGroup = "";
  return (
    <Dialog title="Command palette" onClose={onClose} size="lg" placement="top" bodyClassName="p-0" phone="full"
      header={(
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search size={18} className="text-text-faint" aria-hidden />
          <input data-autofocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Search tasks, projects, or type a command…" aria-label="Search" role="combobox" aria-expanded aria-controls="palette-list"
            aria-activedescendant={items[active] ? `pi-${items[active]!.id}` : undefined}
            className="h-14 min-w-0 flex-1 bg-transparent text-heading outline-none placeholder:text-text-faint" />
          <Kbd className="phone:hidden">Esc</Kbd>
        </div>
      )}>
      <ul ref={listRef} id="palette-list" role="listbox" aria-label="Results" className="max-h-[min(60vh,520px)] overflow-y-auto p-2 phone:max-h-none">
        {items.map((it, i) => {
          const header = it.group !== lastGroup ? (lastGroup = it.group) : null;
          return (
            <li key={it.id} role="presentation">
              {header && <div className="px-2 pb-1 pt-3 text-caption font-semibold uppercase text-text-faint first:pt-1">{header}</div>}
              <div id={`pi-${it.id}`} role="option" aria-selected={i === active} data-index={i} onMouseMove={() => setActive(i)} onClick={() => run(it)}
                className={cx("flex h-10 cursor-pointer items-center gap-3 rounded-control px-2 text-body phone:h-12", i === active ? "bg-hover text-text" : "text-text")}>
                <span className="grid w-5 place-items-center text-text-muted">{it.icon}</span>
                <span className="min-w-0 flex-1 truncate">{it.label}</span>
                {it.hint && (/^[A-Z?]( [A-Z])?$/.test(it.hint) ? <span className="flex gap-1">{it.hint.split(" ").map((k) => <Kbd key={k}>{k}</Kbd>)}</span> : <span className="text-meta text-text-faint">{it.hint}</span>)}
                {i === active && <CornerDownLeft size={14} className="text-text-faint phone:hidden" aria-hidden />}
              </div>
            </li>
          );
        })}
        {!items.length && <li className="px-4 py-8 text-center text-body text-text-faint">No matches</li>}
      </ul>
      <div className="flex items-center gap-4 border-t border-border px-4 py-2 text-meta text-text-faint phone:hidden">
        <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> to move</span>
        <span className="flex items-center gap-1"><Kbd>Enter</Kbd> to open</span>
        <span className="ml-auto flex items-center gap-1"><Inbox size={12} aria-hidden />Tasks, projects, labels and filters</span>
      </div>
    </Dialog>
  );
}

export function ShortcutsHelp({ onClose }: { onClose: () => void }) {
  const groups: [string, [string[], string][]][] = [
    ["Anywhere", [[["Q"], "Add a task"], [["Ctrl", "K"], "Command palette and search"], [["/"], "Search"], [["["], "Collapse or expand the sidebar"], [["?"], "This help"]]],
    ["Go to", [[["G", "I"], "Inbox"], [["G", "T"], "Today"], [["G", "U"], "Upcoming"], [["G", "C"], "Calendar"], [["G", "F"], "Filters & Labels"], [["G", "M"], "Matrix"], [["G", "H"], "Habits"], [["G", "O"], "Focus"], [["G", "P"], "Productivity"], [["G", "S"], "Settings"]]],
    ["Task lists", [[["J"], "Next task"], [["K"], "Previous task"], [["Enter"], "Open the task"], [["E"], "Complete"], [["D"], "Set the date"], [["1"], "Priority 1 (to 4)"], [["X"], "Select"], [["Ctrl", "click"], "Select several"], [["Shift", "click"], "Select a range"], [["Esc"], "Close or clear selection"]]],
    ["Task panel", [[["J"], "Next task"], [["K"], "Previous task"], [["Esc"], "Close"], [["Ctrl", "Enter"], "Post a comment"]]],
  ];
  return (
    <Dialog title="Keyboard shortcuts" onClose={onClose} size="lg">
      <div className="grid gap-x-8 gap-y-6 md:grid-cols-2">
        {groups.map(([title, rows]) => (
          <section key={title}>
            <h3 className="mb-2 text-caption font-semibold uppercase text-text-faint">{title}</h3>
            <dl className="space-y-1.5">
              {rows.map(([keys, what]) => (
                <div key={what} className="flex items-center gap-3 text-body">
                  <dt className="order-2 flex gap-1">{keys.map((k) => <Kbd key={k}>{k}</Kbd>)}</dt>
                  <dd className="order-1 flex-1 text-text-muted">{what}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
